// Onglet « Opérations ».
//
// Ce catalogue est un VOCABULAIRE : la trentaine de verbes du métier,
// pas la liste des usinages rencontrés. Un seul plan lu avait produit
// quatorze lignes — « Perçage », « Perçage 4 × Ø6.5 », « Perçage 4 ×
// Ø6.5 sur entraxe Ø58 » — c'est-à-dire les entités d'une pièce, dont
// aucune ne se répéterait jamais.
//
// L'écran sert donc à confirmer, renommer et FUSIONNER, et à valider ce
// que la lecture des plans propose d'ajouter. Rien n'y entre tout seul.

import { useState } from 'react';
import { Card, CardHead, CardBody } from '../../ui/Card.jsx';
import Table from '../../ui/Table.jsx';
import Button from '../../ui/Button.jsx';
import Badge from '../../ui/Badge.jsx';
import Modal from '../../ui/Modal.jsx';
import { TextField, Select } from '../../ui/TextField.jsx';
import SourceDot from '../../ui/SourceDot.jsx';
import { EmptyState, ErrorState, SkeletonRows } from '../../ui/States.jsx';
import { useCrudList } from './useCrudList.js';
import { api } from '../../lib/api.js';

const EMPTY = { code: '', label: '', description: '', default_machine_id: null, sort_order: 0, aliases: [] };

export function OperationsPanel({ isAdmin, machines }) {
  const list = useCrudList({ path: '/catalogue/operations?all=true', key: 'operations', label: 'Opération' });
  const [merging, setMerging] = useState(null);

  const pending = list.rows.filter((o) => !o.confirmed);

  const columns = [
    {
      key: 'label',
      label: 'Opération',
      render: (o) => (
        <span className="row">
          <SourceDot source={o.confirmed ? 'manual' : 'ai'} title="Lue sur un plan, à confirmer" />
          {o.label}
        </span>
      ),
    },
    {
      key: 'aliases',
      label: 'Alias',
      render: (o) => (o.aliases ?? []).join(', ') || '—',
    },
    { key: 'code', label: 'Code', render: (o) => o.code || '—' },
    {
      key: 'default_machine_id',
      label: 'Machine par défaut',
      render: (o) => machines.find((m) => m.id === o.default_machine_id)?.name ?? '—',
    },
    {
      key: 'active',
      label: '',
      render: (o) => (o.active ? null : <Badge tone="neutral">Désactivée</Badge>),
    },
    ...(isAdmin
      ? [
          {
            key: 'actions',
            label: '',
            render: (o) => (
              <span className="row">
                <Button size="sm" variant="ghost" onClick={() => list.setEditing(o)}>
                  Modifier
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setMerging(o)}>
                  Fusionner
                </Button>
              </span>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Card>
        <CardHead title="Opérations">
          {isAdmin ? (
            <Button size="sm" variant="primary" onClick={() => list.setEditing(EMPTY)}>
              Nouvelle opération
            </Button>
          ) : null}
        </CardHead>
        <CardBody className="stack">
          <p className="field__hint">
            Cette liste est un <strong>vocabulaire</strong> : les verbes du métier — perçage,
            alésage, surfaçage — et non les usinages d’une pièce en particulier. « Perçage » y
            figure ; « Perçage 4 × Ø6.5 sur entraxe Ø58 » n’y figure pas, parce que les quatre
            trous, le Ø6.5 et l’entraxe appartiennent à <em>cette</em> pièce. Ils sont portés par
            la pièce, et la ligne de devis les affiche quand même : « Perçage — 4 × Ø6.5, entraxe
            Ø58 ».
          </p>
          <p className="field__hint">
            C’est ce qui permet au programme d’apprendre. Deux cents perçages répartis sur cent
            pièces enseignent « douze secondes par trou jusqu’à Ø8 en aluminium ». Deux cents
            lignes toutes différentes n’enseignent rien : chacune n’aurait qu’un seul exemple.
          </p>
          <p className="field__hint">
            Quand un plan porte un libellé inconnu, il vous est <strong>proposé</strong> sur
            l’écran de chiffrage — il n’entre ici qu’après votre validation. Un nom contenant un
            chiffre, un « × » ou un « Ø » est refusé : c’est le signe d’une pièce, pas d’un type.
          </p>
          {pending.length > 0 ? (
            <p className="field__hint">
              <strong>{pending.length}</strong> entrée(s) attendent une relecture. Les doublons se
              fusionnent sans casser les devis existants : le libellé perdu devient un alias, donc
              un plan qui le porte retombe sur la bonne ligne.
            </p>
          ) : null}

          {list.loading ? (
            <SkeletonRows rows={4} />
          ) : list.error ? (
            <ErrorState error={list.error} onRetry={list.reload} />
          ) : (
            <Table
              columns={columns}
              rows={list.rows}
              caption="Catalogue des opérations"
              empty={
                <EmptyState title="Aucune opération">
                  Rien à saisir. Le catalogue se construira à partir des premiers plans lus.
                </EmptyState>
              }
            />
          )}
        </CardBody>
      </Card>

      {list.editing ? (
        <OperationForm
          operation={list.editing}
          machines={machines}
          errors={list.errors}
          pending={list.pending}
          onClose={() => list.setEditing(null)}
          onSave={list.save}
        />
      ) : null}

      {merging ? (
        <MergeForm
          operation={merging}
          candidates={list.rows.filter((o) => o.id !== merging.id)}
          onClose={() => setMerging(null)}
          onDone={() => {
            setMerging(null);
            list.reload();
          }}
          toast={list.toast}
        />
      ) : null}
    </>
  );
}

function OperationForm({ operation, machines, errors, pending, onClose, onSave }) {
  const [form, setForm] = useState({ ...operation, aliases: operation.aliases ?? [] });
  const [aliasText, setAliasText] = useState((operation.aliases ?? []).join(', '));
  const set = (patch) => setForm({ ...form, ...patch });

  return (
    <Modal
      open
      onClose={onClose}
      title={operation.id ? form.label : 'Nouvelle opération'}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button variant="primary" type="submit" form="form-operation" disabled={pending}>
            {pending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </>
      }
    >
      <form id="form-operation" className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          onSave(
            { ...form, aliases: aliasText.split(',').map((a) => a.trim()).filter(Boolean) },
            operation.id,
          );
        }}>
        {operation.id && !operation.confirmed ? (
          <p className="field__hint">
            Créée à partir d’un plan. L’enregistrer la confirme.
          </p>
        ) : null}
        <TextField label="Libellé" required value={form.label ?? ''} error={errors.label}
          onChange={(e) => set({ label: e.target.value })} />
        <div className="grid-2">
          <TextField label="Code" value={form.code ?? ''} error={errors.code}
            onChange={(e) => set({ code: e.target.value })} />
          <Select label="Machine par défaut"
            value={form.default_machine_id ? String(form.default_machine_id) : ''}
            error={errors.default_machine_id}
            options={[{ value: '', label: '—' },
              ...machines.map((m) => ({ value: String(m.id), label: m.name }))]}
            onChange={(e) => set({ default_machine_id: e.target.value ? Number(e.target.value) : null })} />
        </div>
        <TextField label="Description" value={form.description ?? ''} error={errors.description}
          onChange={(e) => set({ description: e.target.value })} />
        <TextField label="Alias" value={aliasText}
          hint="Séparés par des virgules. Servent à reconnaître l'opération sur un plan. Une fusion y ajoute automatiquement le libellé absorbé."
          onChange={(e) => setAliasText(e.target.value)} />
      </form>
    </Modal>
  );
}

function MergeForm({ operation, candidates, onClose, onDone, toast }) {
  const [targetId, setTargetId] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    try {
      const res = await api.post(`/catalogue/operations/${operation.id}/fusion`, {
        target_id: Number(targetId),
      });
      toast.success(
        `« ${operation.label} » fusionnée. ${res.movedOperations} gamme(s) basculée(s) sur « ${res.operation.label} ».`,
      );
      onDone();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Fusionner « ${operation.label} »`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Annuler</Button>
          <Button variant="primary" type="submit" form="form-fusion" disabled={busy || !targetId}>
            {busy ? 'Fusion…' : 'Fusionner'}
          </Button>
        </>
      }
    >
      <form id="form-fusion" className="stack" onSubmit={submit}>
        <p className="field__hint">
          « {operation.label} » disparaîtra. Les gammes des devis qui l’utilisent basculeront sur
          l’opération choisie : <strong>aucun devis passé ne perd son opération</strong>, il la voit
          renommée.
        </p>
        <Select label="Conserver à la place" required value={targetId}
          options={[{ value: '', label: '— Choisir —' },
            ...candidates.map((o) => ({ value: String(o.id), label: o.label }))]}
          onChange={(e) => setTargetId(e.target.value)} />
      </form>
    </Modal>
  );
}

export default OperationsPanel;
